import { useEffect, useMemo, useState } from 'react';
import { Tractor, Wrench, Link as LinkIcon, AlertCircle } from 'lucide-react';
import equipmentService from '../../services/equipment';
import { isSprayer } from '../../types/equipment';
import type { Equipment } from '../../types/equipment';

interface SprayEquipmentPickerProps {
  farmIds?: number[];                              // restrict to equipment on these farms (optional)
  sprayerId: number | null;                        // the implement carrying the calibration
  primeMoverId: number | null;                     // the tractor pulling the implement
  onSprayerChange: (id: number | null, sprayer: Equipment | null) => void;
  onPrimeMoverChange: (id: number | null, primeMover: Equipment | null) => void;
}

function isSprayerLike(e: Equipment) {
  // Any sprayer-category machine qualifies — towed/mounted or self-propelled,
  // built-in type or one the org added itself.
  return isSprayer(e);
}

function isTractorLike(e: Equipment) {
  return !!e.can_tow_implements && !e.is_implement;
}

function needsTractor(sprayer: Equipment | null) {
  if (!sprayer) return false;
  // Only towed/mounted implements need a prime mover.
  return !!sprayer.is_implement;
}

function calSummary(eq: Equipment): string {
  const parts: string[] = [];
  if (eq.tank_capacity_litres) parts.push(`${eq.tank_capacity_litres}L tank`);
  if (eq.boom_width_m)         parts.push(`${eq.boom_width_m}m boom`);
  if (eq.output_rate_lpha)     parts.push(`${eq.output_rate_lpha} L/ha`);
  if (eq.nozzle_type)          parts.push(eq.nozzle_type);
  return parts.join(' · ');
}

function SprayEquipmentPicker({
  farmIds, sprayerId, primeMoverId, onSprayerChange, onPrimeMoverChange,
}: SprayEquipmentPickerProps) {
  const [allEquipment, setAllEquipment] = useState<Equipment[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    setLoading(true);
    equipmentService.getActive()
      .then(res => {
        if (res.success && res.data) setAllEquipment(res.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const farmIdSet = useMemo(
    () => new Set((farmIds ?? []).filter(n => Number.isFinite(n))),
    [farmIds],
  );
  const restrictByFarm = !showAll && farmIdSet.size > 0;

  const visible = useMemo(() => {
    if (!restrictByFarm) return allEquipment;
    return allEquipment.filter(e => e.farm_id == null || farmIdSet.has(e.farm_id));
  }, [allEquipment, restrictByFarm, farmIdSet]);

  const sprayers  = visible.filter(isSprayerLike);
  const tractors  = visible.filter(isTractorLike);

  const sprayer    = allEquipment.find(e => e.id === sprayerId)    ?? null;
  const primeMover = allEquipment.find(e => e.id === primeMoverId) ?? null;

  const tractorRequired = needsTractor(sprayer);

  // When the chosen sprayer is hidden by the farm filter, drop it.
  useEffect(() => {
    if (!restrictByFarm) return;
    const visibleIds = new Set(visible.map(e => e.id));
    if (sprayerId    && !visibleIds.has(sprayerId))    onSprayerChange(null, null);
    if (primeMoverId && !visibleIds.has(primeMoverId)) onPrimeMoverChange(null, null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restrictByFarm, allEquipment, farmIds]);

  // If the sprayer doesn't need a tractor (self-propelled / hand-carried),
  // clear any leftover tractor selection.
  useEffect(() => {
    if (!tractorRequired && primeMoverId != null) {
      onPrimeMoverChange(null, null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tractorRequired]);

  // When the sprayer changes, auto-suggest the tractor that is currently
  // attached to it (via equipment_attachments → current_implements on each
  // tractor) — but only if no tractor is set yet.
  useEffect(() => {
    if (!tractorRequired || !sprayer || primeMoverId != null) return;
    const attached = allEquipment.find(t =>
      isTractorLike(t) &&
      (t.current_implements ?? []).some(impl => impl.id === sprayer.id),
    );
    if (attached) onPrimeMoverChange(attached.id, attached);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sprayerId, allEquipment]);

  if (loading) {
    return <p className="text-sm text-neutral-400">Loading equipment…</p>;
  }

  const tractorMismatch = primeMover && !primeMover.can_tow_implements;

  return (
    <div className="space-y-4">
      {farmIdSet.size > 0 && (
        <div className="flex items-center justify-between gap-3 bg-indigo-50 border border-indigo-100 rounded-lg px-3 py-2 text-xs">
          <span className="text-indigo-700">
            {showAll
              ? 'Showing all equipment.'
              : `Showing equipment on the targeted farm${farmIdSet.size > 1 ? 's' : ''}.`}
          </span>
          <label className="flex items-center gap-1.5 cursor-pointer text-indigo-700">
            <input
              type="checkbox"
              checked={showAll}
              onChange={e => setShowAll(e.target.checked)}
              className="rounded border-indigo-300 text-indigo-600 focus:ring-indigo-200"
            />
            Show all
          </label>
        </div>
      )}

      {/* Sprayer (the implement / self-propelled unit) */}
      <div>
        <label htmlFor="spray-sprayer" className="block text-sm font-medium text-neutral-700 mb-1">
          <Wrench className="h-4 w-4 inline mr-1" />
          Sprayer
        </label>
        <select
          id="spray-sprayer"
          value={sprayerId ?? ''}
          onChange={e => {
            const id = e.target.value ? parseInt(e.target.value) : null;
            const next = id ? allEquipment.find(x => x.id === id) ?? null : null;
            onSprayerChange(id, next);
          }}
          className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500"
        >
          <option value="">Select a sprayer…</option>
          {sprayers.map(s => (
            <option key={s.id} value={s.id}>
              {s.name}
              {s.make ? ` (${s.make}${s.model ? ` ${s.model}` : ''})` : ''}
              {s.is_implement ? ' [towed]' : ' [self-propelled]'}
            </option>
          ))}
        </select>
        {sprayers.length === 0 && (
          <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
            <AlertCircle className="h-3.5 w-3.5" />
            No sprayer equipment found. Set the equipment type to a sprayer type
            (Airblast Sprayer, Boom Sprayer, Spray Tank, Knapsack or Drone) in the Equipment module.
          </p>
        )}
        {sprayer && calSummary(sprayer) && (
          <p className="mt-1 text-xs text-neutral-500">
            <span className="font-medium">Calibration:</span> {calSummary(sprayer)}
          </p>
        )}
      </div>

      {/* Prime mover (tractor) — only when the sprayer is a towed implement */}
      {tractorRequired && (
        <div>
          <label htmlFor="spray-prime-mover" className="block text-sm font-medium text-neutral-700 mb-1">
            <Tractor className="h-4 w-4 inline mr-1" />
            Tractor / Prime mover <span className="text-red-500">*</span>
          </label>
          <select
            id="spray-prime-mover"
            value={primeMoverId ?? ''}
            onChange={e => {
              const id = e.target.value ? parseInt(e.target.value) : null;
              const next = id ? allEquipment.find(x => x.id === id) ?? null : null;
              onPrimeMoverChange(id, next);
            }}
            className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500"
          >
            <option value="">Select a tractor…</option>
            {tractors.map(t => (
              <option key={t.id} value={t.id}>
                {t.name}
                {t.make ? ` (${t.make}${t.model ? ` ${t.model}` : ''})` : ''}
                {t.current_hours != null ? ` — ${t.current_hours}h` : ''}
              </option>
            ))}
          </select>
          {tractors.length === 0 && (
            <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
              <AlertCircle className="h-3.5 w-3.5" />
              No tractors found. Mark a vehicle as "can tow implements" in the
              Equipment module.
            </p>
          )}
          {tractorMismatch && (
            <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
              <AlertCircle className="h-3.5 w-3.5" />
              The selected vehicle isn't marked as able to tow implements.
            </p>
          )}
          {sprayer && primeMover && (
            <p className="mt-1 text-xs text-neutral-500 flex items-center gap-1">
              <LinkIcon className="h-3 w-3" />
              {primeMover.name} → {sprayer.name}
            </p>
          )}
        </div>
      )}

      {!tractorRequired && sprayer && (
        <p className="text-xs text-neutral-500 italic">
          Self-propelled — no tractor required.
        </p>
      )}
    </div>
  );
}

export default SprayEquipmentPicker;
